import { Component, signal, computed, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, RouterModule, RouterOutlet } from '@angular/router';
import { LogoutService } from '../core/util/logout.service';
import { environment } from '../../environments/environment';
import { LanguageService } from '../core/interceptor/language.service';
import { HttpClient } from '@angular/common/http';
import { CustomerService } from '../core/services/customer.service';
import { SidebarComponent } from './sidebar/sidebar.component';
import { HeaderComponent } from './header/header.component';
import { ConfirmModalComponent } from '../shared/components/confirm-modal/confirm-modal.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-backoffice',
  standalone: true,
  imports: [RouterOutlet, RouterModule, SidebarComponent, HeaderComponent, ConfirmModalComponent],
  providers: [LogoutService],
  templateUrl: './backoffice.component.html',
})
export class BackofficeComponent implements OnInit, OnDestroy {
  protected endpointPrefix = environment.apiUrl;
  currentUser!: any;
  defaultImage = './../assets/defaultImage.webp';
  customers = signal<any[]>([]);
  selectedCustomer: any;
  customerName = signal<string>('');
  customerLogo = signal<string | null>(null);
  imageUrl = computed(() => this.customerLogo() ?? this.defaultImage);
  isMobileSidebarOpen = false;
  isJubooAgent = false;
  showLogoutModal = false;
  private customerSub?: Subscription;
  private queryParamsSub?: Subscription;

  constructor(
    public logoutService: LogoutService,
    protected router: Router,
    private languageService: LanguageService,
    private http: HttpClient,
    private customerService: CustomerService,
    private activatedRoute: ActivatedRoute
  ) { }

  ngOnInit() {
    this.currentUser = JSON.parse(localStorage.getItem('currentUser') ?? '{}');
    const authorities: string[] = this.currentUser?.authorities || [];
    this.isJubooAgent = authorities.some((elt: string) => elt === 'ROLE_JUBOO_AGENT');
    this.customerService.setIsJubooAgent(this.isJubooAgent);

    this.customerSub = this.customerService.selectedCustomer$.subscribe((customer: any) => {
      if (customer) {
        this.selectedCustomer = customer;
        this.updateImageUrl();
      }
    });

    this.updateImageUrl();
    this.languageService.initializeTranslation();

    this.queryParamsSub = this.activatedRoute.queryParams.subscribe((params) => {
      const customerId = params['customerId'];
      if (customerId) {
        this.resolveCustomerFromId(Number(customerId));
      }
    });

    if (this.isJubooAgent) {
      this.getCustomers();
    }
  }

  ngOnDestroy(): void {
    this.customerSub?.unsubscribe();
    this.queryParamsSub?.unsubscribe();
  }

  private getCustomerIdFromUrl(): number | null {
    const customerId = this.activatedRoute.snapshot.queryParamMap.get('customerId')
    return customerId ? Number(customerId) : null;
  }

  private resolveCustomerFromId(customerId: number): void {
    if (this.selectedCustomer?.id === customerId) {
      return;
    }
    const foundInList = this.customers().find((c) => c.id == customerId);
    if (foundInList) {
      this.selectedCustomer = foundInList;
      this.customerService.setCustomer(foundInList);
      this.updateImageUrl();
      return;
    }
    this.customerService.getCustomerById(customerId).subscribe({
      next: (customer: any) => {
        if (customer) {
          this.selectedCustomer = customer;
          this.customerService.setCustomer(customer);
          this.updateImageUrl();
        }
      },
      error: (err: any) => {
        console.error('Error fetching customer by id in backoffice', err);
      }
    });
  }

  updateImageUrl() {
    if (this.selectedCustomer) {
      this.customerName.set(this.selectedCustomer.name);
      this.customerLogo.set(this.selectedCustomer.logoPath);
    } else if (this.currentUser) {
      this.customerName.set(this.currentUser.customerName || '');
      this.customerLogo.set(this.currentUser.customerLogo || null);
    }
  }

  getCustomers() {
    this.customerService.getCustomers().subscribe(
      (res: any) => {
        this.customers.set(res);
        const customerId = this.getCustomerIdFromUrl();
        if (customerId) {
          const customer = this.customers().find((c) => c.id == customerId);
          if (customer) {
            this.selectedCustomer = customer;
            this.customerService.setCustomer(customer);
            this.updateImageUrl();
          }
        }
      },
      (err: any) => {
        console.error(err);
      }
    );
  }

  selectCustomer(customer: any) {
    this.selectedCustomer = customer;
    this.customerService.setCustomer(customer);
    this.updateImageUrl();
    const queryParams: any = {};
    if (this.isJubooAgent) {
      queryParams.customerId = customer.id;
    }
    this.router.navigate(['backoffice/requestlist'], { queryParams });
  }

  toggleMobileSidebar() {
    this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
  }

  openLogoutModal() {
    this.showLogoutModal = true;
  }

  closeLogoutModal() {
    this.showLogoutModal = false;
  }

  confirmLogout() {
    this.showLogoutModal = false;
    this.logoutService.logout();
  }
}
